import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { useKpiWorkLogForm } from '../../hooks';
import { KpiPanelHeader } from './KpiPanelHeader';
import { KpiLoggingTab } from './KpiLoggingTab';
import { KpiPanelHistory } from './KpiPanelHistory';
import { PageLoader } from '@/components/shared/PageLoader';
import { BasicTabs, TabType } from '@/components/shared/BasicTabs/BasicTabs';
import { KpiPobFilter } from './KpiPobFilter';
import { KpiCostLogWbsFilter } from '../KpiFilters/KpiCostLogWbsFilter';
import { KpiDatePicker } from './KpiDatePicker';
import { KpiShiftSelector } from './KpiShiftSelector';
import * as kpiState from '../../state';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAllCommentsGenericQuery, LogTemplateType } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useGetPlatformId } from '@/hooks/useGetPlatformId';
import { COLLECTION_NAMES } from '@/constants';

export const KpiPanel = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { formik, isSubmitting, template, sections, workpackLogs, logsLoading, isLoading, error } =
    useKpiWorkLogForm();
  const logType = useAtomValue(kpiState.selectedLogTemplateType);
  const shouldShowPobInput = useAtomValue(kpiState.shouldShowPobInput);
  const selectedWorkpackIdForKpiTracker = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const platformId = useGetPlatformId();
  const setWorkpackComments = useSetAtom(kpiState.workpackComments);

  const structureId = useGetStructureId();

  const { loading: commentsLoading, data: commentsData } = useAllCommentsGenericQuery({
    variables: {
      input: {
        collectionName:
          logType === LogTemplateType.CostLogTemplate
            ? COLLECTION_NAMES.PLATFORM
            : COLLECTION_NAMES.WORKPACK,
        referenceId:
          logType === LogTemplateType.CostLogTemplate
            ? platformId || ''
            : (selectedWorkpackIdForKpiTracker as string),
        ...(logType === LogTemplateType.WorklogTemplate && { structureId }),
      },
    },
    skip:
      (logType === LogTemplateType.WorklogTemplate && !selectedWorkpackIdForKpiTracker) ||
      (logType === LogTemplateType.CostLogTemplate && !platformId),
  });

  useEffect(() => {
    if (commentsData?.allComments) {
      setWorkpackComments(commentsData.allComments);
    }
  }, [commentsData?.allComments, setWorkpackComments]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      formik.handleSubmit();
    },
    [formik]
  );

  if (isLoading || logsLoading || commentsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <PageLoader variant="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Stack sx={{ p: 3 }}>
        <Alert severity="error">Failed to load templates. Please try again later.</Alert>
      </Stack>
    );
  }

  if (!template || !sections) {
    return (
      <Stack sx={{ p: 3 }}>
        <Alert severity="info">No template available for this workpack.</Alert>
      </Stack>
    );
  }

  if (!workpackLogs) {
    return (
      <Stack sx={{ p: 3 }}>
        <Alert severity="info">No workpack logs available for this workpack.</Alert>
      </Stack>
    );
  }

  const tabs: TabType[] = [
    {
      index: 0,
      label: 'Log details',
      component: <KpiLoggingTab />,
    },
    ...(logType === LogTemplateType.WorklogTemplate
      ? [
          {
            index: 1,
            label: 'History',
            component: <KpiPanelHistory />,
          },
        ]
      : []),
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <KpiPanelHeader isSubmitting={isSubmitting} isDirty={formik.dirty} isValid={formik.isValid} />

      <Box sx={{ px: 3, pt: 2, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            right: 24,
            top: 16,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {shouldShowPobInput && <KpiPobFilter />}
          {logType === LogTemplateType.CostLogTemplate && <KpiCostLogWbsFilter />}
          <KpiDatePicker />
          {logType === LogTemplateType.WorklogTemplate && <KpiShiftSelector />}
        </Box>
        <BasicTabs
          tabs={tabs}
          selectedTab={selectedTab}
          changeTab={setSelectedTab}
          variant="default"
          fullWidth={false}
          hideScrollGuide
          tabsStyles={{
            '& button': {
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 400,
              color: abyssColors.primary[400],
              minWidth: 'auto',
              px: 3,
            },
            '& .Mui-selected': {
              fontWeight: 500,
              color: `${abyssColors.primary[500]} !important`,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: abyssColors.primary[500],
              height: '3px',
              borderRadius: '3px 3px 0 0',
            },
          }}
        />
      </Box>
    </Box>
  );
};
